// utils/oauthHelper.js
import { googleOAuthService } from '../services/googleOAuthService.ts';
import { setLocalStorage, getStoredToken, isTokenExpired, getUser, clearStorage } from './localStorageManager.js';

export const getUserId = () => {
    const user = getUser();
    if (!user?.id) throw new Error('No user ID found in local storage.');
    return user.id;
};

export const getUserEmail = () => {
    const user = getUser();
    if (!user?.email) throw new Error('No user email found in local storage.');
    return user.email;
};

export const handleGoogleLoginResponse = async (data) => {
    if (data.redirect && data.oauth_url) {
        window.location.href = data.oauth_url;
        return null;
    }
    if (data.jwt) {
        setLocalStorage(data.jwt, data.expires_in);
        return;
    }

    throw new Error('Invalid login response from backend.');
};

export const handleGoogleCallbackFlow = async ({ code, user_id, user_email }) => {
    const data = await googleOAuthService.callback({ code, user_id, user_email });

    if (!data.jwt || !data.user_id || !data.user_email) {
        throw new Error('Invalid callback response from backend.');
    }

    setLocalStorage(data.jwt, data.expires_in, data.user_id, data.user_email);
    return { id: data.user_id, email: data.user_email };
};

export const handleGoogleRefreshFlow = async () => {
    const data = await googleOAuthService.refreshToken();

    if (data.redirect && data.oauth_url) {
        // Throw error with redirect URL so interceptor can handle it
        const error = new Error('Token refresh requires OAuth redirect');
        error.oauth_url = data.oauth_url;
        error.redirect = true;
        throw error;
    }

    if (!data.jwt) throw new Error('Failed to refresh token: No JWT in response.');
    
    // Get existing user info from localStorage (refresh endpoint doesn't return user_id/email)
    const existingUser = getUser();
    setLocalStorage(
        data.jwt, 
        data.expires_in, 
        existingUser?.id || data.user_id, 
        existingUser?.email || data.user_email
    );
};

export const getToken = async () => {
    const token = getStoredToken();
    if (!token) return null;

    if (isTokenExpired()) {
        console.warn("Token expired. Attempting refresh...");
        try {
            await handleGoogleRefreshFlow();
            return token;
        } catch (err) {
            console.error("Token refresh failed:", err);
            clearStorage();
            return null;
        }
    }

    return token;
};
